
import {Component, Inject, Prop, Provide, Vue, Watch} from "vue-property-decorator";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {mapState} from "vuex";

import vToggleSwitch from "./vToggleSwitch.vue";
import vIngredientsDisplayNames from "./vIngredientsDisplayNames.vue";
import vUSContainsPercentOfForm from "./vUSContainsPercentOfForm.vue";
import vAccordion from "./vAccordion.vue";
import {ExportLabelState} from "@/models/NutritionLabels/ExportLabelState";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {Recipe} from "@/models/Recipes/Recipe";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {debounce, unionByKey} from "@/utils/Helpers";
import {DietaryTypes} from "@/models/NutritionLabels/DietaryTypes";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import IngredientDisplay from "@/models/Ingredient/IngredientDisplay";
import FreeTextPlaceholder from "@/models/NutritionLabels/FreeTextPlaceholder";
import VRecipeNutritionLabelPrintout from "@/components/vRecipeNutritionLabelPrintout.vue";
import VBanner from "@/components/vBanner.vue";
import vCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {buildComplexIngredientName, buildSimpleIngredientName} from "@/services/ExportLabel.service";
import VCard from "@/components/bootstrap/vCard.vue";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import LabelTypeSelection from "@/components/labelConfiguration/LabelTypeSelection.vue";
import LabelConfiguration from "@/components/labelConfiguration/LabelConfiguration.vue";
import FreeTextFieldsConfiguration from "@/components/labelConfiguration/FreeTextFieldsConfiguration.vue";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {Allergens} from "@/models/NutritionLabels/Allergens";
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";
import DietaryConfiguration from "@/components/labelConfiguration/DietaryConfiguration.vue";
import {LabelRenderConfiguration} from "@/models/NutritionLabels/v2/LabelRenderConfiguration";
import {LabelConfigurationResponse} from "@/models/NutritionLabels/v2/LabelConfigurationResponse";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {LabelConfigurationModel} from "@/models/NutritionLabels/v2/LabelConfigurationModel";
import {LabelIngredientConfiguration} from "@/models/NutritionLabels/v2/LabelIngredientConfiguration";
import {FreeTextFieldConfiguration} from "@/models/NutritionLabels/v2/FreeTextFieldConfiguration";
import {LabelRenderDto} from "@/models/NutritionLabels/v2/RenderLabelDto";
import {NutritionLabelDownloadFileFormat} from "@/models/NutritionLabels/NutritionLabelDownloadFileFormat";
import {FreeTextAttributeType} from "@/models/NutritionLabels/v2/enums/FreeTextAttributeType";
import {SaveCustomizedLabelDataDto} from "@/models/NutritionLabels/v2/SaveCustomizedLabelDataDto";
import {vSelectOptionBase} from "@/typings/common";
import {parseCountryKey, splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";
import IngredientConfiguration from "@/components/labelConfiguration/IngredientConfiguration.vue";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import {
  getCoreNutrients, getCoreSupplementNutrients,
  getSupplementNutrients,
  getVoluntaryNutrients
} from "@/models/NutritionLabels/v2/collections/NutrientGroups";
import { type BelowLabelConfiguration } from "@/models/NutritionLabels/v2/BelowLabelConfiguration";
import {NutritionCountry} from "@/models/NutritionLabels/NutritionCountry";
import TextEditorConfiguration from "@/components/labelConfiguration/TextEditorConfiguration.vue";
import { BrandingConfig } from "@/models/NutritionLabels/BrandingConfig";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";

@Component({
  components: {
    VUpsellModal,
    TextEditorConfiguration,
    DietaryConfiguration,
    FreeTextFieldsConfiguration,
    LabelConfiguration,
    IngredientConfiguration,
    LabelTypeSelection,
    VCard,
    VRecipeNutritionLabelPrintout,
    VBanner,
    vToggleSwitch,
    vIngredientsDisplayNames,
    vUSContainsPercentOfForm,
    vAccordion,
    vCautionaryInputs,
    CollapsableCard,
    AllergensConfiguration,
  },
  computed: {
    DietaryTypes() {
      return DietaryTypes;
    },
    LabelLanguage() {
      return LabelLanguage;
    },
    NutrientType() {
      return NutrientType;
    },
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
export default class vBackOfPackageConfig extends Vue {
  LabelTypeMetadata = LabelTypeMetadata;
  localizationState!: LocalizationState;

  @Prop() recipe!: Recipe;
  @Prop() packageLabelType!: LabelTypeMetadata;
  @Prop() preselectedLabelType: LabelTypeMetadata;

  /**
   * Rounding rule system to use for the given country
   */
  @Prop() roundingRuleIndex!: number;

  @Prop({
    default: (): RecipeCompositionMeasurement[] => {
      return [];
    }
  }) subrecipeMeasurements!: RecipeCompositionMeasurement[];

  @Inject("fetchPreview") fetchPreviewV2: (renderData: LabelRenderDto, requestingScreen: ExportLabelView) => Promise<Blob>;
  @Inject("bopConfigRef") readonly bopConfigRef!: any;

  savedLabelConfig: SaveCustomizedLabelDataDto = null;
  model: ExportLabelState = new ExportLabelState();
  alreadyApplied = false;
  hasSavedData = false;
  showTrialUpsellModal = false;

  subRecipeDisplays: IngredientDisplay[] = [];
  selectedLabelSubType: vSelectOptionBase<string> = null;
  selectedAllergens: Allergens[] = [];
  mayContainText: Partial<Record<LabelLanguage, string[]>> = {};
  selectedDietaryTypes: DietaryTypes[] = [];
  brandingConfiguration: BrandingConfig = {
    showLogo: false,
    logoPosition: "top-left",
    textTop: "",
    textBottom: "",
    showImage: false,
    imagePosition: "bottom-left"
  };
  requestedLabelConfigurations: string[] = [];
  config: LabelRenderConfiguration = {
    recipes: { enabled: false, mode: null },
    nutrients: { sugarAlcohols: false, voluntary: false, supplement: false },
    dimensions: { width: false },
  };
  labelConfiguration: LabelConfigurationModel = {
    hideOtherText: false,
    showShortFooter: false,
    showSideBySidePanel: false,
    useMcgUnit: false,
    showAmountInSeparateColumn: false,
    switchToElectrolyteMode: false,
    showCarbsDV: false,
    combineVoluntaryNutrients: false,
    useCondensedFont: false,
    useInlineFootnote: false,
    widthCm: 0,
    selectedCoreNutrients: [],
    selectedSupplementNutrients: [],
    selectedVoluntaryNutrients: [],
    useCondensedFormat: false,
    hideTotalText: false,
    switchCalorieBreakdownFootnote: false,
    switchServingSizeText: false,
    includeMonoPoly: false,
    includeOmegas: false,
    includeTextualIndicators: false,
    includeTrafficLightColoring: false,
    showProteinDV: false,
    shouldDisplayCalories449Text: false,
    shouldDisplayNutrientsSideBySide: false,
    showCalorieFromFatText: false,
    shouldDisplaySugarAlcohol: false,
    showThcLogo: false,
    labelTypes: {},
    includeRi: false,
    includeSaltFootnote: false,
    includePerServingAnalysis: false,
    useParForFrenchServingSize: false,
    proprietaryBlends: [],
    recipes: [],
    useNormalServingSizeFont: false
  };

  freeTextFields: FreeTextPlaceholder[] = [];

  labelIngredientConfiguration = new LabelIngredientConfiguration();

  supplementNutrients = [
    NutrientType.Biotin,
    NutrientType.Choline,
    NutrientType.Niacin,
    NutrientType.PantothenicAcid,
    NutrientType.Riboflavin,
    NutrientType.Thiamine,
    NutrientType.VitaminA,
    NutrientType.VitaminB6,
    NutrientType.VitaminB12,
    NutrientType.VitaminC,
    NutrientType.VitaminD,
    NutrientType.VitaminE,
    NutrientType.Calcium,
    NutrientType.Chromium,
    NutrientType.Copper,
    NutrientType.Iodine,
    NutrientType.Magnesium,
    NutrientType.Molybdenum,
    NutrientType.Phosphorus,
    NutrientType.Potassium,
    NutrientType.Selenium,
    NutrientType.Zinc,
    NutrientType.LAlanine,
    NutrientType.LArginine,
    NutrientType.LAsparagine,
    NutrientType.LAsparticAcid,
    NutrientType.LCysteine,
    NutrientType.LGlutamicAcid,
    NutrientType.LGlutamine,
    NutrientType.Glycine,
    NutrientType.LHistidine,
    NutrientType.LIsoleucine,
    NutrientType.LLeucine,
    NutrientType.LLysine,
    NutrientType.LMethionine,
    NutrientType.LPhenylalanine,
    NutrientType.LProline,
    NutrientType.LSerine,
    NutrientType.LThreonine,
    NutrientType.LTyrosine,
    NutrientType.LValine,
    NutrientType.Caffeine
  ];

  hasNormalizedFreeTextPlaceholders = false;

  @Watch("savedLabelConfig", { immediate: true })
  onSavedLabelConfigLoaded(val: SaveCustomizedLabelDataDto | null) {

    this.hasSavedData = !!val;

    if(!this.hasSavedData){
      this.labelConfiguration.selectedCoreNutrients = getCoreNutrients(this.recipe.nutritionCountry.id);
    }
    if (!val) return;

    const parsed = this.parsedModelData;

    this.alreadyApplied = true;
    this.setCountryBasedLanguage(this.recipe.nutritionCountry.name);
  }

  @Watch("model.freeTextPlaceholders")
  initFreeTextFields() {
    if (!this.hasNormalizedFreeTextPlaceholders) {
      const savedFreeTextFields: FreeTextPlaceholder[] = this.parsedModelData.freeTextFields ?? [];
      const labelFreeTextFields: FreeTextPlaceholder[] = this.model.freeTextPlaceholders ?? [];

      if (!labelFreeTextFields.length) return;

      const savedFieldNames = savedFreeTextFields.map((ftf) => ftf.displayText);
      const nonSavedPlaceholders = labelFreeTextFields.filter((ftf) => !savedFieldNames.includes(ftf.displayText));

      this.freeTextFields = [
        ...savedFreeTextFields,
        ...nonSavedPlaceholders
      ];

      this.hasNormalizedFreeTextPlaceholders = true;
      this.fetchPreview();
    }
  }

  @Watch("model.labelTypes", { deep: true })
  onCautionarySopDataChanged(newVal: any) {
    this.$emit("csopDataChanged", this.model.labelTypes);
  }

  @Watch("selectedLabelSubType")
  onLabelTypeChange() {
    // if(!this.hasSavedData){
    //   if(this.selectedLabelSubType?.value === "SupplementLabel") {
    //     this.labelConfiguration.selectedCoreNutrients = getCoreSupplementNutrients(this.recipe.nutritionCountry.id);
    //   }
    //   else {
    //     this.labelConfiguration.selectedCoreNutrients = getCoreNutrients(this.recipe.nutritionCountry.id);
    //   }
    // }

    this.updateRecipeMetadataChange(
      this.selectedLabelSubType?.value === "SupplementLabel"
    );
  }

  @Watch("recipe.nutritionCountry", { immediate: true, deep: true })
  onCountryChange(newCountry: NutritionCountry | null) {
    if (!newCountry) return;
    const countryName = newCountry.name;

    this.setCountryBasedLanguage(countryName);
  }

  setCountryBasedLanguage(countryName : string){
    if (countryName === "UK") {
      this.model.primaryLanguage = LabelLanguage.English;
    }
    else {
      // Other countries
      const savedLanguage: LabelLanguage | undefined = this.parsedModelData?.language;

      if (savedLanguage) {
        this.model.primaryLanguage = savedLanguage;
      }
      else if (!this.model.primaryLanguage) {
        this.model.primaryLanguage = LabelLanguage.English;
      }
    }
    this.$emit("languageChange", this.model.primaryLanguage);
  }

  // Handle new label flow, no prior saved label
  async created() {
    this.labelIngredientConfiguration.shouldExpandSubrecipes = this.recipe.nutritionCountry.id == NutritionLabelCountry.UK;
    this.ingredientAndSubRecipeDisplaysInit();
  }

  get translations() {
    return this.localizationState.translations;
  }

  get isTrialUser() {
    return !!this.$store.getters.getUserDetails.isTrial;
  }

  get isHydrated() {
    return this.savedLabelConfig != null;
  }

  get hasOnlyCautionary(){
    if(this.model.hasValidCautionary && this.packageLabelType != LabelTypeMetadata.FrontOfPack){
      return true;
    }
  }

  get ingredientAndSubRecipeDisplays(){
    if(this.labelIngredientConfiguration.shouldExpandSubrecipes) {
      return this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList;
    }

    else {
      return this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList;
    }
  }

  get savedLabelType() {
    if (this.savedLabelConfig?.labelTemplate) {
      if(this.preselectedLabelType == LabelTypeMetadata.SupplementFacts){
        return "SupplementLabel";
      }
      return this.savedLabelConfig.labelTemplate;
    }

    switch (this.preselectedLabelType) {
    case LabelTypeMetadata.SupplementFacts:
      return "SupplementLabel";

    case LabelTypeMetadata.FSIS:
      this.labelConfiguration.switchServingSizeText = true;
      this.labelConfiguration.showCalorieFromFatText = true;
      this.labelConfiguration.hideTotalText = true;
      this.labelConfiguration.switchCalorieBreakdownFootnote = true;
      this.labelConfiguration.useNormalServingSizeFont = true;
      this.labelConfiguration.shouldDisplayNutrientsSideBySide = true;
      return "StandardVerticalLabel";

    default:
      return "StandardVerticalLabel";
    }
  }

  get shouldExpandLabelConfiguration() {
    return this.selectedLabelSubType?.value === 'AggregateLabel'
      || this.selectedLabelSubType?.value === 'DualColumnLabel';
  }

  get parsedModelData() {
    const data = this.savedLabelConfig?.modelData;

    if (!data) return {};
    // check if it's a string, if so, try to parse it as JSON
    if (typeof data === "string") {
      try {
        let parsedModel = JSON.parse(data);

        if (parsedModel.freeTextFields && !Array.isArray(parsedModel.freeTextFields)) {
          const freeTextObj = parsedModel.freeTextFields as FreeTextFieldConfiguration;

          parsedModel.freeTextFields = Object.entries(freeTextObj).map(
            ([displayText, content], index) => ({
              id: index + 1,
              displayText: displayText as FreeTextAttributeType,
              content: content || []
            })
          );

          // Filter freeTextFields to only the ones requested
          parsedModel.freeTextFields = parsedModel.freeTextFields.filter(
            (ftf: FreeTextPlaceholder) => this.model.freeTextPlaceholders.map((ft) => ft.displayText).includes(ftf.displayText)
          );
        }

        this.labelConfiguration.selectedCoreNutrients = parsedModel.coreNutrients || [];
        this.labelConfiguration.selectedSupplementNutrients = parsedModel.supplementNutrients?.filter((n: NutrientType) =>
          getSupplementNutrients(this.recipe.nutritionCountry.id).includes(n)
        ) || [];
        this.labelConfiguration.selectedVoluntaryNutrients = parsedModel.voluntaryNutrients.filter((n: NutrientType) =>
          getVoluntaryNutrients(this.recipe.nutritionCountry.id).includes(n)
        ) || [];

        if (!this.alreadyApplied) {
          this.model.primaryLanguage = parsedModel.language;
          this.labelConfiguration = {
            ...this.labelConfiguration,
            ...parsedModel
          };

          if (parsedModel.brandingConfiguration) {
            this.brandingConfiguration = {
              showLogo: parsedModel.brandingConfiguration.showLogo ?? false,
              logoPosition: parsedModel.brandingConfiguration.logoPosition ?? "top-left",
              textTop: parsedModel.brandingConfiguration.textTop ?? {},
              textBottom: parsedModel.brandingConfiguration.textBottom ?? {},
              showImage: parsedModel.brandingConfiguration.showImage ?? false,
              imagePosition: parsedModel.brandingConfiguration.imagePosition ?? "bottom-left",
            };
          }

          if (parsedModel.belowLabelConfiguration) {
            const blc = parsedModel.belowLabelConfiguration;

            this.selectedAllergens = blc.allergens || [];
            this.mayContainText = this.convertMayContainArrayToMap(
              blc.mayContain || [],
              parsedModel.language
            );
            this.selectedDietaryTypes = blc.dietaryLabels || [];

            this.labelIngredientConfiguration.includeIngredients = blc.shouldDisplayIngredients ?? this.labelIngredientConfiguration.includeIngredients;
            this.labelIngredientConfiguration.shouldExpandSubrecipes = blc.shouldExpandSubrecipes ?? this.labelIngredientConfiguration.shouldExpandSubrecipes;

            if (blc.ingredientDisplayModels) {
              this.populateSavedDisplays(blc);
            }
          }
          this.alreadyApplied = true;
        }

        return parsedModel;
      }

      catch (err) {
        console.error(err);
        return {};
      }
    }

    // already an object
    return data;
  }

  convertMayContainArrayToMap(
    value: string[][],
    language: keyof typeof LabelLanguage
  ): Partial<Record<LabelLanguage, string[]>> {
    const langs = splitLanguages(LabelLanguage[language]) as LabelLanguage[];

    const result: Partial<Record<LabelLanguage, string[]>> = {};

    langs.forEach((lang, index) => {
      result[lang] = [...(value[index] ?? [])];
    });

    return result;
  }

  populateSavedDisplays(blc: BelowLabelConfiguration) {
    if (this.labelIngredientConfiguration.shouldExpandSubrecipes) {
      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = unionByKey(
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList,
        blc.ingredientDisplayModels,
        "ingredientId"
      );
    }

    else {
      const ingredientsOnly = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.filter(
        (itm) => Boolean(itm.ingredientId)
      );
      const subrecipesOnly = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.filter(
        (itm) => Boolean(itm.recipeId)
      );

      const savedIngredientsOnly = blc.ingredientDisplayModels.filter((itm) => (
        Boolean(itm.ingredientId)
        && ingredientsOnly.map((disp) => disp.ingredientId).includes(itm.ingredientId)
      ));

      const savedRecipesOnly = blc.ingredientDisplayModels.filter((itm) => (
        Boolean(itm.recipeId)
        && subrecipesOnly.map((disp) => disp.recipeId).includes(itm.recipeId)
      ));

      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = [
        ...unionByKey(ingredientsOnly, savedIngredientsOnly, "ingredientId"),
        ...unionByKey(subrecipesOnly, savedRecipesOnly, "recipeId")
      ];
    }
  }

  handleMayContainUpdate(
    updated: Partial<Record<LabelLanguage, string[]>>
  ) {
    const activeLanguages = splitLanguages(
      LabelLanguage[this.model.primaryLanguage]
    ) as LabelLanguage[];

    const cleaned: Partial<Record<LabelLanguage, string[]>> = {};

    for (const lang of activeLanguages) {
      if (updated[lang]) {
        cleaned[lang] = [...updated[lang]];
      }
    }

    this.mayContainText = cleaned;
  }


  handleCautionaryQualification(state: boolean) {
    this.$emit("cautionaryEligibility", state);
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
  }

  handleTrialCardExpandAttempt(event: MouseEvent) {
    if (!this.isTrialUser) {
      return;
    }

    const targetElement = event.target as HTMLElement | null;
    const cardHeader = targetElement?.closest(".card-title") as HTMLElement | null;

    if (!cardHeader) {
      return;
    }

    const chevron = cardHeader.querySelector(".chevron") as HTMLElement | null;
    const isCollapsed = !!chevron && !chevron.classList.contains("active");

    if (!isCollapsed) {
      return;
    }

    this.showTrialUpsellModal = true;
  }

  updateRecipeMetadataChange(isSupplement: boolean) {
    this.$emit("updateRecipeMetadata", isSupplement);
  }

  async updateLanguageSelection(language: LabelLanguage) {
    this.model.primaryLanguage = language;
    this.$emit("languageChange", language);
    await this.requestLabelConfigurations();
    await this.fetchPreview();
  }

  get countryKey() {
    return parseCountryKey(this.recipe.nutritionCountry.name);
  }
  async requestLabelConfigurations() {
    const response = await NutritionLabelRepository.getConfigurationOptionsForLabelV2(
      this.countryKey,
      this.selectedLabelSubType.value
    );

    const {
      freeTextFields = [],
      configurationOptions = []
    } = response.data as LabelConfigurationResponse;

    this.requestedLabelConfigurations = configurationOptions;

    this.model.freeTextPlaceholders = freeTextFields.map<FreeTextPlaceholder>((field, index) => ({
      id: index + 1,
      displayText: field as FreeTextAttributeType,
      content: [] as string[],
    }));
  }

  get freeTextConfiguration(): FreeTextFieldConfiguration {
    const rv: FreeTextFieldConfiguration = {};

    for (const placeholder of this.freeTextFields || []) {
      rv[placeholder.displayText] = placeholder.content;
    }

    return rv;
  }

  ingredientAndSubRecipeDisplaysInit() {
    this.subRecipeDisplays = this.labelIngredientConfiguration.getSubRecipeDisplays(this.recipe, this.subrecipeMeasurements);

    if(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.length == 0){
      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = this.labelIngredientConfiguration.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        true
      );
    }

    if(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.length == 0){
      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = this.labelIngredientConfiguration.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        false
      );
    }
  }

  /**
   * Resets the display text for a specific subrecipe based on its ID.
   *
   * @param {number} subrecipeId - The unique identifier of the subrecipe to reset its display.
   * @return {void} This method does not return a value.
   */
  resetSubrecipeDisplay(subrecipeId: number) {
    const subrecipeDisplay = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.find(
      (x) => x.recipeId == subrecipeId
    );

    if (subrecipeDisplay) {
      const recipe = this.recipe.childRecipes.find((x) => x.recipe.id == subrecipeId);
      subrecipeDisplay.displayTextInPrimaryLanguage = buildComplexIngredientName(recipe.recipe);
    }
  }

  resetIngredientDisplay(ingredientId: number) {
    const combinedIngredientDisplay = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.find(
      (x) => x.ingredientId == ingredientId
    );
    const expandedIngredientDisplay = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.find(
      (x) => x.ingredientId == ingredientId
    );

    const recipeIngredient = this.recipe.ingredients.find((x) => x.ingredient.id == ingredientId);

    if (combinedIngredientDisplay) {
      combinedIngredientDisplay.displayTextInPrimaryLanguage = buildSimpleIngredientName(recipeIngredient.ingredient);
    }

    if (expandedIngredientDisplay) {
      expandedIngredientDisplay.displayTextInPrimaryLanguage = buildSimpleIngredientName(recipeIngredient.ingredient);
    }
  }

  /**
   * Identifies and returns a list of ingredients or subrecipes whose display names
   * have been changed from their default or expected values.
   *
   * @return {Array<{type: 'ingredient' | 'subrecipe', id: number}>} An array of objects representing items with changed display names.
   * Each object contains the type ('ingredient' or 'subrecipe') and the corresponding ID of the item.
   */
  get getChangedDisplayNames() {
    const resettableDisplays: { type: 'ingredient' | 'subrecipe'; id: number }[] = [];

    for (const ingredientDisplay of this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList) {
      const isIngredient = Boolean(ingredientDisplay.ingredientId);

      if (isIngredient) {
        const recipeIngredient = this.recipe.ingredients.find((x) => x.ingredient.id == ingredientDisplay.ingredientId);

        if(recipeIngredient) { // Temp fix for missing ingredients
          if (ingredientDisplay.displayTextInPrimaryLanguage != buildSimpleIngredientName(recipeIngredient.ingredient)) {
            resettableDisplays.push({type: 'ingredient', id: ingredientDisplay.ingredientId});
          }
        }
      }
      else {
        const subrecipe = this.recipe.childRecipes.find((x) => x.recipe.id == ingredientDisplay.recipeId);

        if (ingredientDisplay.displayTextInPrimaryLanguage != buildComplexIngredientName(subrecipe.recipe)) {
          resettableDisplays.push({type: 'subrecipe', id: ingredientDisplay.recipeId});
        }
      }
    }

    return resettableDisplays;
  }

  async handleResetModel() {
    this.model.bopCautionaryPreview = "";
    this.model.hasValidCautionary = false;
  }

  debouncedFetchPreview() {
    debounce(this.fetchPreview, 1000, "back-of-package-config-label-update");
  }

  toggleSubrecipeExpansion() {
    this.labelIngredientConfiguration.shouldExpandSubrecipes = !this.labelIngredientConfiguration.shouldExpandSubrecipes;

    if(this.labelIngredientConfiguration.shouldExpandSubrecipes){
      this.updateIngredientName();
    }
    else{
      this.copyIngredientNamesOver();
    }

    this.debouncedFetchPreview();
  }

  copyIngredientNamesOver(){
    this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.forEach((expandedItem) => {
      const itemIndex = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.findIndex((i) => i.ingredientId === expandedItem.ingredientId);

      if (itemIndex !== -1) {
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayTextInSecondaryLanguage = expandedItem.displayTextInSecondaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayTextInPrimaryLanguage = expandedItem.displayTextInPrimaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayText = expandedItem.displayText;
      }
    });
  }

  updateIngredientName(){
    this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.forEach((expandedItem) => {
      const itemIndex = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.findIndex((i) => i.ingredientId === expandedItem.ingredientId);

      if (itemIndex !== -1) {
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayTextInSecondaryLanguage = expandedItem.displayTextInSecondaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayTextInPrimaryLanguage = expandedItem.displayTextInPrimaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayText = expandedItem.displayText;
      }
    });
  }

  get renderModel() {
    return {
      country: this.countryKey,
      labelName: this.selectedLabelSubType.value,
      renderModel: {
        language: this.model.primaryLanguage,
        shouldDisplaySugarAlcohol: this.labelConfiguration.shouldDisplaySugarAlcohol || false,
        shouldDisplayNutrientsSideBySide: this.labelConfiguration.shouldDisplayNutrientsSideBySide || false,
        shouldDisplayCalories449Text: this.labelConfiguration.shouldDisplayCalories449Text || false,
        includeTextualIndicators: this.labelConfiguration.includeTextualIndicators || false,
        showShortFooter: this.labelConfiguration.showShortFooter || false,
        showSideBySidePanel: this.labelConfiguration.showSideBySidePanel || false,

        switchServingSizeText: this.labelConfiguration.switchServingSizeText || false,
        showCalorieFromFatText: this.labelConfiguration.showCalorieFromFatText || false,
        switchCalorieBreakdownFootnote: this.labelConfiguration.switchCalorieBreakdownFootnote || false,
        hideTotalText: this.labelConfiguration.hideTotalText || false,
        useNormalServingSizeFont: this.labelConfiguration.useNormalServingSizeFont || false,
        useMcgUnit: this.labelConfiguration.useMcgUnit || false,

        showAmountInSeparateColumn: this.labelConfiguration.showAmountInSeparateColumn || false,
        switchToElectrolyteMode: this.labelConfiguration.switchToElectrolyteMode || false,
        hideOtherText: this.labelConfiguration.hideOtherText || false,

        widthCm: this.labelConfiguration.widthCm || 7,
        freeTextFields: this.freeTextConfiguration,
        recipeId: this.recipe.id,
        recipes: this.labelConfiguration.recipes || [],

        coreNutrients: this.labelConfiguration.selectedCoreNutrients,
        supplementNutrients: this.labelConfiguration.selectedSupplementNutrients || [""],
        voluntaryNutrients: this.labelConfiguration.selectedVoluntaryNutrients || [],
        proprietaryBlends: this.labelConfiguration.proprietaryBlends || [],

        showProteinDV: this.labelConfiguration.showProteinDV || false,
        showCarbsDV: this.labelConfiguration.showCarbsDV || false,
        showThcLogo: this.labelConfiguration.showThcLogo,
        includeRi: this.labelConfiguration.includeRi,
        includeSaltFootnote: this.labelConfiguration.includeSaltFootnote,
        includePerServingAnalysis: this.labelConfiguration.includePerServingAnalysis,
        includeTrafficLightColoring: this.labelConfiguration.includeTrafficLightColoring,
        includeMonoPoly: this.labelConfiguration.includeMonoPoly,
        includeOmegas: this.labelConfiguration.includeOmegas,

        useCondensedFont: this.labelConfiguration.useCondensedFont,
        useCondensedFormat: this.labelConfiguration.useCondensedFormat,
        combineVoluntaryNutrients: this.labelConfiguration.combineVoluntaryNutrients,
        useInlineFootnote: this.labelConfiguration.useInlineFootnote,
        useParForFrenchServingSize: this.labelConfiguration.useParForFrenchServingSize,

        brandingConfiguration: {
          showLogo: this.brandingConfiguration.showLogo,
          logoPosition: this.brandingConfiguration.logoPosition,
          textTop: this.brandingConfiguration.textTop,
          textBottom: this.brandingConfiguration.textBottom,
          showImage: this.brandingConfiguration.showImage,
          imagePosition: this.brandingConfiguration.imagePosition
        },

        belowLabelConfiguration: {
          allergens: this.selectedAllergens || [],
          mayContain: this.normalizeMayContain(this.mayContainText) || [],
          ingredientDisplayModels: this.labelIngredientConfiguration.shouldExpandSubrecipes
            ? this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
            : this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
          shouldDisplayIngredients: this.labelIngredientConfiguration.includeIngredients,
          isCommaSeparated: true,
          shouldDisplayColon: true,
          shouldExpandSubrecipes: this.labelIngredientConfiguration.shouldExpandSubrecipes,
          dietaryLabels: this.selectedDietaryTypes,
          thresholdPercent: this.labelIngredientConfiguration.thresholdPercent
        }
      }
    };
  }

  // normalizeMayContain(value: Record<string, string[]>): string[][] {
  //   return Object.values(value).filter((v) => v.length > 0);
  // }

  normalizeMayContain(value: Record<string, string[]>): string[][] {
    return Object.values(value)
      .map((v) => [...v])
      .filter((v) => v.length > 0);
  }

  @Provide("bopFetchPreview")
  async fetchPreview(downloadFormat = NutritionLabelDownloadFileFormat.png) {
    try {
      return await this.fetchPreviewV2(
        {
          format: downloadFormat,
          ...this.renderModel
        },
        ExportLabelView.BOP
      );
    }

    catch (error) {
      console.error("Failed to fetch preview:", error);
    }
  }
}
